import { useState } from 'react';

type Deserialize<T> = (data: any) => T;

interface UseStorageOptions<T> {
  deserialize?: Deserialize<T>;
  forceDefaultValue?: boolean;
}

export function useLocalStorage<T>(
  key: string | undefined,
  defaultValue: T,
  options: UseStorageOptions<T> = {}
): [T, (val: T) => void] {
  return useStorage(key, defaultValue, options, window.localStorage);
}

export function useSessionStorage<T>(
  key: string | undefined,
  defaultValue: T,
  options: UseStorageOptions<T> = {}
): [T, (val: T) => void] {
  return useStorage(key, defaultValue, options, window.sessionStorage);
}

function useStorage<T>(
  key: string | undefined,
  defaultValue: T,
  options: UseStorageOptions<T>,
  storage: Storage
): [T, (val: T) => void] {
  const disableStorage = key === undefined;

  const [storedValue, setStoredValue] = useState<T>(() => {
    if (disableStorage || options.forceDefaultValue) {
      return defaultValue;
    }
    try {
      const item = storage.getItem(key);
      if (!item) {
        return defaultValue;
      }
      const json = JSON.parse(item);
      return typeof options.deserialize === 'function'
        ? options.deserialize(json)
        : json;
    } catch {
      return defaultValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (disableStorage) {
        return;
      }
      if (valueToStore === undefined) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch {
      // ignore write errors
    }
  };

  return [storedValue, setValue];
}
